import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';

import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { AutomationIcon, CloseIcon, CloudDownloadIcon, MyTaskIcon } from '@sketchnote/icons';
import { Avatar, priorityOptions } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import { ColDef, ColumnState, GridApi } from 'ag-grid-enterprise';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { use } from 'chai';
import cn from 'classnames';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import { filter, omit } from 'lodash';
import debounce from 'lodash/debounce';
import pick from 'lodash/pick';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTimerWidgetStore } from 'src/components/navbar/TimerWidget/useTimerWidgetStore';
import { useKanbanStore } from 'src/features/kanban';
import { TaskEdit } from 'src/features/tasks';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getFormattedValue } from 'src/utils/getFormattedValue';
import { shallow } from 'zustand/shallow';

import { AutomationTabs } from '../../components/file-explore/components/Automation/automation-tabs';
import { useBoardStore } from '../boards';
import { BulkActions } from '../boards/components/BulkActions';
import { useFileStore } from '../files/store/useFileStore';
import {
  filterData,
  FilterOption,
  FilterSelectOption,
  FilterValue,
  TableFilters,
} from '../filters';
import { Column } from '../kanban/component/Column/types';
import { DeleteBoardItem, MoveBoardItem } from '../kanban/component/Item';
import { KanbanState } from '../kanban/store/types';
import { DeleteField } from '../tasks/components/DeleteField';
import { useTasksStore } from '../tasks/store/useTasksStore';
import { AddField } from './AddField';
import {
  AssigneeCellEditor,
  DateCellEditor,
  LabelCellEditor,
  MoreActionsCellEditor,
  PriorityCellEditor,
  StatusCellEditor,
} from './CellEditors';
import { CustomFieldRenderer } from './CellEditors/CustomFieldEditor';
import { MarkCompleteCellRenderer } from './CellEditors/MarkCompleteCellRenderer';
import {
  ApprovalRenderer,
  LabelRenderer,
  OpenTaskColumnButton,
  RowSelectionRenderer,
} from './ListCellRenders';
import { useTableStore } from './store';
import styles from './styles/ListGridView.module.css';

export const ListGridView = ({ id }: { id: string }) => {
  const { onChangeCurrentSelectedItem, currentSelectedItem, onCloseCurrentSelectedItem } =
    useKanbanStore(boardSelector, shallow);
  const {
    columns,
    setItem,
    onMarkAsComplete,
    onMarkAsCompleteSubTask,
    onAddNewItem,
    onUpdateItemField,
    fields,
    fetchLabel,
    onDeleteItem,
    onDuplicateItem,
    onDeleteCustomField,
    setFields,
    isReadOnly,
    setReadOnly,
  } = useTableStore(
    (s) => ({
      columns: s.columns,
      setItem: s.setItem,
      onMarkAsComplete: s.onMarkAsComplete,
      onMarkAsCompleteSubTask: s.onMarkAsCompleteSubTask,
      onAddNewItem: s.onAddNewItem,
      onUpdateItemField: s.onUpdateItemField,
      fields: s.fields,
      fetchLabel: s.fetchLabel,
      onDeleteItem: s.onDeleteItem,
      onDuplicateItem: s.onDuplicateItem,
      onDeleteCustomField: s.onDeleteCustomField,
      setFields: s.setFields,
      isReadOnly: s.isReadOnly,
      setReadOnly: s.setReadOnly,
    }),
    shallow
  );
  const router = useRouter();

  // const boardData = useBoardStore();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const boardId = useMemo(() => router?.query?.view, [router?.query?.view]);
  const [isGridReady, setGridIsReady] = useState<boolean>(false);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const [isFieldDeletePromptOpen, setIsFieldDeletePromptOpen] = useState<boolean>(false);
  const [selectedCustomField, setSelectedCustomField] = useState<string>('');
  const [isDuplicatePromptOpen, setIsDuplicatePromptOpen] = useState<boolean>(false);
  const [newTask, setNewTask] = useState('');
  const taskInputRef = useRef<HTMLInputElement>(null);
  const gridApi = useRef<AgGridReact>(null);
  const [isCreatingTask, setIsCreatingTask] = useState<boolean>(false);
  const [openSnack] = useSnackbar();
  const [isAutomateModalOpen, setIsAutomateModalOpen] = useState(false);
  const [showMoveFilesDialog, setShowMoveFilesDialog] = useState(false);
  const [isBulkActionOpen, setIsBulkActionOpen] = useState(false);
  const [showDeleteFilesDialog, setShowDeleteFilesDialog] = useState(false);
  const { selectedItems, clearSelectedItems } = useBoardStore((s) => ({
    selectedItems: s.selectedItems,
    clearSelectedItems: s.clearSelectedItems,
  }));
  const { fetchTimers } = useTimerWidgetStore((s) => ({
    fetchTimers: s.fetchTimers,
  }));
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  useEffect(() => {
    return () => {
      clearSelectedItems();
    };
  }, []);

  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const onUpdateViewConfig = useFileStore((s) => s.onUpdateViewConfig);
  const viewId = router.query.viewId as string;
  const fileId = router.query.slug as string;

  const isPrimaryFile = useMemo(
    () => viewId === currentFile?.primaryFile?.id,
    [currentFile?.primaryFile?.id, viewId]
  );

  const currentView = isPrimaryFile
    ? currentFile?.primaryFile
    : currentFile?.internalFiles?.find((i) => i.viewId === viewId);

  const viewConfig = currentView?.config || {};
  const tableColumnsConfig = viewConfig?.tableColumnsConfig || {};

  useEffect(() => {
    if (!isGridReady) return;
    setTimeout(() => {
      if (
        tableColumnsConfig.filtersState &&
        Object.keys(tableColumnsConfig.filtersState).length > 0
      ) {
        setFilterValues(tableColumnsConfig.filtersState);
      } else setFilterValues({});
      if (tableColumnsConfig.columnState) {
        const columnState = tableColumnsConfig.columnState;
        gridApi.current?.api?.applyColumnState({
          applyOrder: true,
          state: columnState,
        });
      } else {
        // apply default column state
        const columnState = gridApi.current?.api?.getColumnState();
        gridApi.current?.api?.applyColumnState({
          applyOrder: true,
          state: columnState?.map(
            (i) =>
              ({
                colId: i.colId,
                hide: null,
                pinned: null,
                flex: null,
              } as ColumnState)
          ),
        });
      }
    }, 100);
  }, [viewId, isGridReady, fields]);

  const { isPublicFile } = useAuth((s) => ({
    isPublicFile: s.isPublicFile,
  }));

  useEffect(() => {
    useTableStore.setState({ boardTableId: id });
    fetchLabel({ id: id as string });
    let isReadOnly = currentFile?.access === 'view' || (isPublicFile as boolean);
    if (typeof isReadOnly === 'undefined') isReadOnly = false;
    setReadOnly(isReadOnly);
  }, []);

  const columnDataTransformer = useCallback(
    (columnData: Column[]) => {
      const itemReduce = (columnItem) =>
        columnItem.items.reduce((a, c) => {
          const itemColumn = columnData.find((col) => col.id === c.column);
          return a.concat(
            Object.assign(
              {},
              c,
              {
                status: itemColumn?.name,
                color: itemColumn?.color,
                colId: itemColumn?.id,
                colName: itemColumn?.name,
              },
              c.fields?.reduce((acc, curr) => {
                const newObj = {
                  [curr.fieldId]: pick(curr, ['data', 'type', 'label', 'fieldId']),
                };
                return Object.assign({}, acc, newObj);
              }, {})
            )
          );
        }, []);

      const reducedAndFlattenedColumnItems = columnData?.map(itemReduce).flat();

      const transformedColumnItems: AgGridReactProps['rowData'] =
        reducedAndFlattenedColumnItems?.map((item) => ({
          ...item,
          name: getFormattedValue(item.name),
        }));

      const newItems = transformedColumnItems
        .map((t) =>
          [
            t,
            (t.subtasks || []).map((i) => {
              const itemColumn = columnData.find((col) => col.id === i.column);
              return Object.assign(
                {},
                i,
                {
                  name: getFormattedValue(i.name),
                  colId: itemColumn?.id,
                  colName: itemColumn?.name,
                  color: itemColumn?.color,
                  parentColumn: t.colId,
                },
                i.fields?.reduce((acc, curr) => {
                  const newObj = {
                    [curr.fieldId]: pick(curr, ['data', 'type', 'label', 'fieldId']),
                  };
                  return Object.assign({}, acc, newObj);
                }, {})
              );
            }),
          ].flat()
        )
        .flat()
        .map((i) => Object.assign({}, i, { path: i.parent ? [i.parent, i.id] : [i.id] }))
        .sort((a, b) => a.number - b.number);
      // return transformedColumnItems;

      const fieldIds = fields.map((i) => i.fieldId);
      const itemsPickList = [
        'id',
        'name',
        'completed',
        'colId',
        'columnId',
        'colName',
        'color',
        'assignee',
        'assignDate',
        'priority',
        'approvalStatus',
        'label',
        'path',
        'parent',
        'isSubtask',
        'isLoading',
        'fields',
        'parentColumn',
      ].concat(fieldIds);

      const filteredOutTasks = newItems.map((i) => pick(i, itemsPickList));
      return filteredOutTasks;
    },
    [fields]
  );

  const autoGroupColumnDef: ColDef = useMemo(() => {
    return {
      headerName: 'Task',
      editable: ({ data }) => (data.isLoading ? false : !isReadOnly),
      lockPosition: true,
      initialFlex: 1,
      minWidth: 200,
      valueGetter: ({ data }) => {
        if (data?.isLoading) return 'Creating Subtask...';
        return data?.name || '';
      },
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }, []);

  const allColumnItems = useMemo(() => columnDataTransformer(columns), [columns]);

  // const isOptimizedModeOn = useMemo(() => allColumnItems.length > 100, [allColumnItems]);,

  const suppressEnter = (params) => {
    const KEY_ENTER = 'Enter';
    const event = params.event;
    const key = event.key;
    const suppress = key === KEY_ENTER;
    return suppress;
  };

  const handleOpenTask = (itemId) => {
    const link = isPublicFile
      ? `/${mode}/${orgId}/files/${router.query.slug}/public`
      : `/${mode}/${orgId}/files/${router.query.slug}`;

    const query = {
      ...omit(router.query, ['slug', 'mode', 'orgId']),
      taskId: itemId,
    };
    router.push({
      pathname: link,
      query: query,
    });
  };

  const handleTaskViewClose = () => {
    const link = isPublicFile
      ? `/${mode}/${orgId}/files/${router.query.slug}/public`
      : `/${mode}/${orgId}/files/${router.query.slug}`;

    const query = omit(router.query, ['slug', 'mode', 'orgId', 'taskId']);

    // TODO: Remove api call and use store
    fetchLabel({ id: id as string });

    router.push({
      pathname: link,
      query: query,
    });
  };

  const derivedColumns: AgGridReactProps['columnDefs'] = [
    !isPublicFile &&
      !isReadOnly && {
        field: 'rowSelected',
        headerName: 'Select Task',
        initialWidth: 45,
        maxWidth: 45,
        cellRenderer: RowSelectionRenderer,
        resizable: false,
      },
    {
      field: 'id',
      headerName: 'Open Task',
      cellRenderer: OpenTaskColumnButton,
      initialWidth: 45,
      maxWidth: 45,
      onCellClicked: ({ value }) => {
        handleOpenTask(value);
      },
      resizable: false,
    },
    {
      field: 'completed',
      cellRenderer: MarkCompleteCellRenderer,
      width: 40,
      maxWidth: 40,
      tooltipValueGetter: ({ value }) => {
        return value ? 'Mark as incomplete' : 'Mark as complete';
      },
      onCellClicked: ({ data }) => {
        if (isReadOnly) return;
        if (data.parent) {
          onMarkAsCompleteSubTask({
            columnId: data.colId,
            parentId: data.parent,
            itemId: data.id,
            completed: !data.completed,
          });
        } else {
          onMarkAsComplete({
            columnId: data.colId,
            itemId: data.id,
            completed: !data.completed,
          });
        }
      },
      resizable: false,
    },
    !isReadOnly && {
      field: 'id',
      headerName: 'More Actions',
      valueFormatter: () => '···',
      width: 50,
      maxWidth: 50,
      editable: !isReadOnly,
      singleClickEdit: true,
      cellEditorPopup: true,
      cellEditor: MoreActionsCellEditor,
      cellEditorPopupPosition: 'under',
      cellStyle: {
        cursor: 'pointer',
      },
      // suppressMovable: true,
      resizable: false,
    },
    {
      field: 'colName',
      headerName: 'Status',
      // enableRowGroup: true,
      cellStyle: (params) => {
        if (params?.data?.color) {
          return {
            backgroundColor: params?.data?.color,
            textAlign: 'center',
          };
        } else return {};
      },
      editable: ({ data }) => !isReadOnly,
      cellEditor: StatusCellEditor,
      singleClickEdit: true,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      suppressKeyboardEvent: (params) => {
        return suppressEnter(params);
      },
    },
    {
      field: 'assignee',
      headerName: 'Assigned to',
      valueGetter: ({ data }) => {
        return (
          data?.assignee?.filter((i) => i?.email || i?.firstname).map((i) => i?.firstname || '') ||
          ''
        );
      },
      // valueFormatter: ({ value }) => {
      //   return (
      //     value
      //       ?.filter((i) => i?.email || i?.firstname)
      //       .map((i) => i?.firstname || '')
      //       .join(', ') || ''
      //   );
      // },
      editable: !isReadOnly,
      cellEditor: AssigneeCellEditor,
      singleClickEdit: true,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      // valueGetter: ({ data }) => data.assignee.map((i) => i.firstname).join(', ') || '-',
      // cellRenderer: AssigneeCellRenderer,
    },
    {
      field: 'assignDate',
      headerName: 'Due date',
      // valueGetter: ({ data }) =>
      //   data.assignDate.map((i) => new Date(i).toLocaleDateString()).join('-') || '-',
      valueFormatter: ({ value }) => {
        if (!value) return '';
        return value?.map((i) => format(new Date(i), 'do MMM yyyy')).join('-') || '';
      },
      cellEditor: DateCellEditor,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      editable: !isReadOnly,
      singleClickEdit: true,
      // cellRenderer: DateCellRenderer,
    },
    {
      field: 'approvalStatus',
      headerName: 'Approval status',
      cellRenderer: ApprovalRenderer,
      valueFormatter: ({ value }) =>
        value?.charAt(0).toUpperCase() + value?.slice(1).toLowerCase() || '',

      editable: false,
    },
    {
      field: 'priority',
      valueFormatter: ({ value }) => {
        if (!value) return '';
        return value?.charAt(0).toUpperCase() + value?.slice(1).toLowerCase() || '';
      },
      cellStyle: (params) => {
        if (params?.data?.priority) {
          let backgroundColor = '#FFFFFF';
          let color = '#000000';
          const selectedPriorityOption = priorityOptions.find(
            (p) => p.value === params?.data?.priority
          );
          if (selectedPriorityOption) {
            backgroundColor = selectedPriorityOption.bgColor;
            color = selectedPriorityOption.color;
          }

          return {
            backgroundColor,
            color,
            textAlign: 'center',
          };
        } else return {};
      },
      editable: !isReadOnly,
      cellEditor: PriorityCellEditor,
      singleClickEdit: true,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
    },
    {
      field: 'label',
      valueGetter: ({ data }) => {
        return data?.label?.map((i) => i.label) || [];
      },
      // valueFormatter: ({ value }) => value?.join(', ') || '',
      cellRenderer: LabelRenderer,
      editable: !isReadOnly,
      cellEditor: LabelCellEditor,
      singleClickEdit: true,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      suppressKeyboardEvent: (params) => {
        return suppressEnter(params);
      },
    },
  ].filter(Boolean) as ColDef[];

  useEffect(() => {
    setRowData(
      allColumnItems.map((rowData) => ({
        ...rowData,
        rowSelected: selectedItems.includes(rowData.id) || selectedItems.includes(rowData.parent),
      }))
    );
  }, [allColumnItems, selectedItems]);

  const setColumnsAsPerConfig = (columnsConfig) => {
    const columnState = columnsConfig?.columnState || [];
    const columnOrder = columnState.map((i) => i.colId);
    if (fields.length > 0) {
      const newFields = fields.map((field) => {
        return CustomFieldRenderer(field, isReadOnly);
      }) as any;
      let allColumns: any[] = [...newFields];
      derivedColumns.forEach((column: any) => {
        allColumns.push(column);
      });
      if (columnOrder) {
        allColumns = allColumns.sort((a, b) => {
          const aIndex = columnOrder.indexOf(a.field);
          const bIndex = columnOrder.indexOf(b.field);
          return aIndex - bIndex;
        });
      }
      setColumnDefs(allColumns);
    } else {
      let allColumns: any[] = [];
      derivedColumns.forEach((column: any) => {
        allColumns.push(column);
      });
      if (columnOrder) {
        allColumns = allColumns.sort((a, b) => {
          const aIndex = columnOrder.indexOf(a.field);
          const bIndex = columnOrder.indexOf(b.field);
          return aIndex - bIndex;
        });
      }
      setColumnDefs(allColumns);
    }
  };

  useEffect(() => {
    setColumnsAsPerConfig(tableColumnsConfig);
  }, [viewId, fields]);

  const [rowData, setRowData] = useState<AgGridReactProps['rowData'] | null>(null);
  const [columnDefs, setColumnDefs] = useState<AgGridReactProps['columnDefs']>([]);
  useEffect(() => {
    if (router?.query?.taskId) {
      const foundTask = columns
        .reduce((acc, column) => {
          return [...acc, ...column.items];
        }, [])
        .find((item) => item.id === router.query.taskId);

      if (foundTask) {
        // fix for incorrect column id in the task
        const foundColumn = columns
          .map((c) => Object.assign({}, c, { items: c.items.map((i) => i.id) }))
          .find((i) => i.items.includes(foundTask.id));
        if (foundColumn) {
          onChangeCurrentSelectedItem({
            isOpen: true,
            columnId: foundColumn.id,
            mode: 'edit',
            item: foundTask,
          });
        }
      }
    }
  }, [router?.query?.taskId]);

  const onCellEditRequest = useCallback(
    ({
      data: value,
      oldValue,
      newValue,
      column,
    }: {
      data: any;
      oldValue: any;
      newValue: any;
      column: any;
    }) => {
      const columnId = column?.colId;

      const colId = value?.isSubtask ? value?.parentColumn : value?.colId;
      switch (columnId) {
        case 'name':
          {
            if (value && newValue) {
              if (value?.id) {
                const name = [
                  {
                    type: 'p',
                    children: [
                      {
                        text: newValue,
                      },
                    ],
                  },
                ];
                onUpdateItemField({
                  item: {
                    name,
                    id: value.id,
                  },
                  keys: ['name'],
                });
                setItem({
                  columnId: colId,
                  item: { ...value, name },
                  itemId: value.id,
                });
                useTasksStore.setState((s) => {
                  if (!s.tasks[value.id]) return;
                  s.tasks[value.id] = {
                    ...s.tasks[value.id],
                    name,
                  };
                });
              }
            }
          }
          break;
        case 'ag-Grid-AutoColumn':
          {
            if (value && newValue) {
              if (value?.id) {
                const name = [
                  {
                    type: 'p',
                    children: [
                      {
                        text: newValue,
                      },
                    ],
                  },
                ];
                onUpdateItemField({
                  item: {
                    name,
                    id: value.id,
                  },
                  keys: ['name'],
                });
                setItem({
                  columnId: colId,
                  item: { ...value, name },
                  itemId: value.id,
                });
                useTasksStore.setState((s) => {
                  if (!s.tasks[value.id]) return;
                  s.tasks[value.id] = {
                    ...s.tasks[value.id],
                    name,
                  };
                });
              }
            }
          }
          break;
        case 'completed':
          {
            if (value.parent) {
              onMarkAsCompleteSubTask({
                columnId: colId,
                parentId: value.parent,
                itemId: value.id,
                completed: newValue,
              });
            } else {
              onMarkAsComplete({
                columnId: colId,
                itemId: value.id,
                completed: newValue,
              });
            }
          }
          break;
        case 'colId':
          {
            const destColId = newValue;
            const sourceColId = oldValue;

            const sourceIndex =
              columns
                .find((i) => i.id === sourceColId)
                ?.items.findIndex((i) => i.id === value.id) || 0;
          }
          break;
        default:
          break;
      }
    },
    []
  );

  const handleCloseAddTask = () => {
    onCloseCurrentSelectedItem();
  };

  const handleAddTask = async (e) => {
    setIsCreatingTask(true);
    e.preventDefault();
    if (currentSelectedItem.columnId) {
      try {
        const payload: any = {};
        payload.boardId = boardId;
        payload.columnKey = currentSelectedItem.columnId;
        payload.name = [{ type: 'p', children: [{ text: newTask }] }];
        currentSelectedItem.top ? (payload.top = true) : '';

        await onAddNewItem(payload);

        setIsCreatingTask(false);
        onCloseCurrentSelectedItem();
        setNewTask('');
      } catch (error) {
        console.error(error);
        openSnack({
          text: 'Error creating task!',
          snackType: 'error',
          position: 'top-center',
          duration: 2000,
        });
      }
    }
  };

  const onToggleDeletePrompt =
    (isOpen = true) =>
    () => {
      setIsDeletePromptOpen(isOpen);
    };
  const onToggleDuplicatePrompt =
    (isOpen = true) =>
    () => {
      setIsDuplicatePromptOpen(isOpen);
    };

  const getDataPath = useMemo(() => {
    return (data) => {
      return data.path;
    };
  }, []);

  const handleDeleteTask = async () => {
    if (currentSelectedItem.item?.id && currentSelectedItem.columnId) {
      await onDeleteItem({
        columnId: currentSelectedItem.columnId,
        itemId: currentSelectedItem.item.id,
      });
      setIsDeletePromptOpen(false);
      fetchTimers();
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${id}&type=${router.query.type}&viewId=${router.query.viewId}`
      );
    }
  };

  const handleDuplicateTask = () => {
    if (currentSelectedItem.item?.id && currentSelectedItem.columnId) {
      onDuplicateItem({
        columnId: currentSelectedItem.columnId,
        itemId: currentSelectedItem.item.id,
      });
      setIsDuplicatePromptOpen(false);
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${id}&type=${router.query.type}&viewId=${router.query.viewId}`
      );
    }
  };

  const getMainMenuItems = useCallback((params) => {
    const fieldIdList = fields.map((i) => i.fieldId);
    if (fieldIdList.includes(params.column.getId())) {
      return [
        ...params.defaultItems,
        {
          name: 'Delete',
          action: () => {
            setSelectedCustomField(params.column.getId());
            setIsFieldDeletePromptOpen(true);
          },
        },
      ];
    }
    return [...params.defaultItems];
  }, []);

  const handleDeleteCustomField = useCallback(() => {
    // Need to remove dependency of having a task id for field removal
    if (rowData?.[0]) {
      onDeleteCustomField({
        taskId: rowData[0].id,
        fieldId: selectedCustomField,
      });

      setSelectedCustomField('');
      setIsFieldDeletePromptOpen(false);
    }
  }, [rowData, selectedCustomField]);

  useEffect(() => {
    if (!isGridReady) return;
    debouncedColumnConfigUpdate(
      { ...tableColumnsConfig, filtersState: filterValues },
      {
        fileId,
        viewId,
        viewConfig,
        isPrimaryFile,
      }
    );
  }, [filterValues]);

  const handleChangeColumn = (e) => {
    // const columnApi: ColumnApi = e.columnApi;
    // if (!columnApi || !gridApi) return;
    const gridApi: GridApi = e.api;
    if (!gridApi) return;
    const columnState = gridApi.getColumnState();
    const tableColumnsConfig = {
      ...viewConfig?.tableColumnsConfig,
      filtersState: filterValues,
      columnState: columnState,
    };
    debouncedColumnConfigUpdate(tableColumnsConfig, {
      fileId,
      viewId,
      viewConfig,
      isPrimaryFile,
    });
  };

  const debouncedColumnConfigUpdate = useCallback(
    debounce(async (tableColumnsConfig, { fileId, viewId, viewConfig, isPrimaryFile }) => {
      onUpdateViewConfig({
        fileId,
        viewId,
        config: {
          ...viewConfig,
          tableColumnsConfig,
        },
        isPrimaryFile: isPrimaryFile,
      });
    }, 500),
    []
  );

  const onClickExport = () => {
    if (gridApi.current) {
      const barredCols = ['completed', 'id', 'id_1'];
      const columnKeys = gridApi.current.api
        .getAllDisplayedColumns()
        .map((column) => column.getColId())
        .filter((col) => !barredCols.includes(col));
      gridApi.current.api.exportDataAsCsv({
        columnKeys,
      });
    }
  };

  const handleBulkActionMenuClick = (value: string) => {
    if (value === 'move') {
      setShowMoveFilesDialog(true);
    } else if (value === 'delete') {
      setShowDeleteFilesDialog(true);
    }
  };

  const toggleBulkAction = (value?: boolean) => () => {
    setIsBulkActionOpen(value ?? !isBulkActionOpen);
  };

  const filterDefs = useMemo(() => {
    const assigneeOptions: FilterSelectOption[] = [];
    const labelsOptions: FilterSelectOption[] = [];

    if (rowData && Array.isArray(rowData)) {
      const allAssignees = rowData?.map((r) => r.assignee).flat() || [];
      const assigneeIds = allAssignees.map((i) => i?.memberId);
      const uniqueAssigneeIds = [...new Set(assigneeIds)].filter(Boolean);
      uniqueAssigneeIds.forEach((id) => {
        const assignee = allAssignees.find((i) => i?.memberId === id);
        if (assignee)
          assigneeOptions.push({
            label: assignee.firstname,
            value: assignee.memberId,
            icon: <Avatar size="xs1" name={assignee.firstname} src={assignee.avatar} />,
          });
      });

      for (const rowIndex in rowData) {
        const row = rowData[rowIndex];
        for (const labelIndex in row.label) {
          const label = row.label[labelIndex];
          if (label && !labelsOptions.some((op) => op.value === label.id))
            labelsOptions.push({
              label: label.label,
              value: label.id,
              icon: <div className="w-12 h-12" style={{ backgroundColor: label.color }} />,
            });
        }
      }
    }

    const filterOptions: FilterOption[] = [
      {
        filterId: 'name',
        filterType: 'text',
        filterLabel: 'Task name',
        searchPlaceholder: 'Search by task name',
        filterIcon: <MyTaskIcon />,
      },
      {
        filterId: 'completed',
        filterType: 'select',
        filterLabel: 'Completion status',
        filterIcon: <MyTaskIcon />,
        filterOptions: [
          { label: 'Complete', value: true },
          { label: 'Incomplete', value: false },
        ],
      },
      {
        filterId: 'colId',
        filterType: 'select',
        filterLabel: 'Status',
        filterIcon: <MyTaskIcon />,
        filterOptions: columns.map((col) => {
          return {
            label: col.name,
            value: col.id,
          };
        }),
      },
      {
        filterId: 'assignee',
        filterType: 'select',
        filterLabel: 'Assigned to',
        filterIcon: <MyTaskIcon />,
        filterOptions: assigneeOptions,
        selectObjectKey: 'memberId',
        includeUnassigned: true,
      },
      {
        filterId: 'assignDate',
        filterType: 'dateRange',
        filterLabel: 'Due date',
        filterIcon: <MyTaskIcon />,
        includeUnassigned: true,
      },
      {
        filterId: 'approvalStatus',
        filterType: 'select',
        filterLabel: 'Approval status',
        filterIcon: <MyTaskIcon />,
        filterOptions: [
          { label: 'Approved', value: 'approved' },
          { label: 'Pending', value: 'pending' },
          { label: 'Rejected', value: 'rejected' },
        ],
        includeUnassigned: true,
      },
      {
        filterId: 'priority',
        filterType: 'select',
        filterLabel: 'Priority',
        filterIcon: <MyTaskIcon />,
        filterOptions: priorityOptions.slice(1),
        includeUnassigned: true,
        unassignedText: 'No priority',
      },
      {
        filterId: 'label',
        filterType: 'select',
        filterLabel: 'Label',
        filterIcon: <MyTaskIcon />,
        filterOptions: labelsOptions,
        selectObjectKey: 'id',
        includeUnassigned: true,
      },
    ];
    const customFieldValueModifier = (val) => val?.data;
    fields.forEach(
      (field: {
        fieldId: string;
        label: string;
        type:
          | 'text'
          | 'number'
          | 'select'
          | 'multiselect'
          | 'date'
          | 'person'
          | 'url'
          | 'email'
          | 'phone';
      }) => {
        if (['text', 'phone', 'email', 'url'].includes(field.type)) {
          filterOptions.push({
            filterId: field.fieldId,
            filterType: 'text',
            filterLabel: field.label,
            searchPlaceholder: `Search by ${field.label}`,
            valueModifier: customFieldValueModifier,
          });
        } else if (field.type === 'number') {
          filterOptions.push({
            filterId: field.fieldId,
            filterType: 'numberRange',
            filterLabel: field.label,
            valueModifier: customFieldValueModifier,
          });
        } else if (['select', 'multiselect', 'person'].includes(field.type)) {
          const fieldOptions: FilterSelectOption[] = [];

          for (const rowIndex in rowData) {
            const row = rowData[rowIndex];
            if (row[field.fieldId]) {
              for (const fieldIndex in row[field.fieldId].data) {
                const fieldItem = row[field.fieldId].data[fieldIndex];
                if (field.type === 'person') {
                  if (fieldItem && !fieldOptions.some((op) => op.value === fieldItem.memberId))
                    fieldOptions.push({
                      label: fieldItem.firstname,
                      value: fieldItem.memberId,
                      icon: <Avatar size="xs1" name={fieldItem.firstname} src={fieldItem.avatar} />,
                    });
                } else if (fieldItem && !fieldOptions.some((op) => op.value === fieldItem.value))
                  fieldOptions.push({
                    label: fieldItem.value,
                    value: fieldItem.value,
                    icon: fieldItem.color ? (
                      <div className="w-12 h-12" style={{ backgroundColor: fieldItem.color }} />
                    ) : null,
                  });
              }
            }
          }

          filterOptions.push({
            filterId: field.fieldId,
            filterType: 'select',
            filterLabel: field.label,
            filterOptions: fieldOptions,
            includeUnassigned: true,
            valueModifier: customFieldValueModifier,
            selectObjectKey: field.type === 'person' ? 'memberId' : 'value',
          });
        } else if (field.type === 'date') {
          filterOptions.push({
            filterId: field.fieldId,
            filterType: 'dateRange',
            filterLabel: field.label,
            includeUnassigned: true,
            valueModifier: customFieldValueModifier,
          });
        }
      }
    );

    return filterOptions;
  }, [fields, rowData]);

  const handleFilterNodes = useCallback(
    (node: any) => filterData(node.data, filterValues, filterDefs),
    [filterValues, filterDefs]
  );

  const handleFilterChange = useCallback((filterId: string, value: FilterValue) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  }, []);
  const handleFilterClear = useCallback((filterId: string) => {
    setFilterValues((prev) => {
      const { [filterId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  return (
    <div>
      {!isReadOnly && (
        <div className="flex justify-between m-auto gap-8" style={{ width: '90%' }}>
          <Modal
            isOpen={showMoveFilesDialog}
            onClose={() => setShowMoveFilesDialog(false)}
            motionPreset="none"
            isCentered
          >
            <ModalOverlay />
            <ModalContent minW={650} width={650} className="max-w-650 bg-white rounded-lg">
              <MoveBoardItem
                selectedItems={selectedItems}
                onCancel={() => setShowMoveFilesDialog(false)}
              />
            </ModalContent>
          </Modal>
          <Modal
            isOpen={showDeleteFilesDialog}
            onClose={() => setShowDeleteFilesDialog(false)}
            motionPreset="none"
            isCentered
          >
            <ModalOverlay />
            <ModalContent minW={650} width={650} className="max-w-650 bg-white rounded-lg">
              <DeleteBoardItem
                selectedItems={selectedItems}
                onCancel={() => setShowDeleteFilesDialog(false)}
              />
            </ModalContent>
          </Modal>
          <div className="flex h-50 items-center gap-8">
            {currentSelectedItem.mode === 'add' ? (
              <form className="flex" onSubmit={handleAddTask}>
                <input
                  className=" border rounded-sm w-330 mr-12 px-12 py-5 text-sm"
                  type="text"
                  value={newTask}
                  onChange={(e) => {
                    setNewTask(e.target.value);
                  }}
                  placeholder="Untitled task"
                  ref={taskInputRef}
                  disabled={isCreatingTask}
                />
                <button
                  type="submit"
                  className={cn(
                    'bg-gray-200 px-12 text-xs text-gray-700 hover:bg-gray-300 border border-gray-300 rounded-sm mr-6',
                    {
                      ' animate-pulse': isCreatingTask,
                    }
                  )}
                  disabled={isCreatingTask || newTask === ''}
                >
                  {isCreatingTask ? 'Saving...' : 'Save'}
                </button>
                <button onClick={handleCloseAddTask} disabled={isCreatingTask}>
                  <CloseIcon className="ml-5 text-gray-500 text-xss hover:text-gray-600" />
                </button>
              </form>
            ) : (
              <button
                className="flex bg-gray-200 hover:bg-gray-300 border border-gray-300 items-center text-sm text-gray-700 px-8 py-2 rounded-sm"
                tabIndex={-1}
                onClick={() => {
                  onChangeCurrentSelectedItem({
                    isOpen: true,
                    columnId: columns[0].id,
                    itemIndex: 0,
                    mode: 'add',
                  });
                  setTimeout(() => {
                    taskInputRef.current?.focus();
                  }, 200);
                  // handleTaskViewOpen(column.id, undefined, 'add');
                }}
              >
                {/* <AddIcon wrapperClass="text-gray-700 text-sm mr-6" /> */}
                <span className="font-bold text-base mr-6">+</span> Task
              </button>
            )}
          </div>
          <div className="flex items-center gap-8">
            {selectedItems.length > 0 && (
              <BulkActions
                onClickMenuItem={handleBulkActionMenuClick}
                selectedItems={selectedItems}
              />
            )}
            {/** TODO: FILTERS HERE */}
            <TableFilters
              filterOptions={filterDefs}
              activeFilters={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onClearAllFilters={() => setFilterValues({})}
            />
            <button
              className="flex items-center mr-9 pr-9 border-r border-dashed border-primary-500"
              onClick={() => setIsAutomateModalOpen(true)}
            >
              <AutomationIcon fill="#254DDA" />
              <span className="text-primary-500 font-medium text-sm ml-4">Automation</span>
            </button>
            <button className="flex items-center mr-4" onClick={onClickExport}>
              <Tippy content={<span className="text-xs">Export as CSV</span>}>
                <div className="flex w-full h-full justify-center items-center cursor-pointer text-primary-500 hover:text-primary-700">
                  <CloudDownloadIcon />
                </div>
              </Tippy>
            </button>
          </div>
          <AutomationTabs
            isAutomateModalOpen={isAutomateModalOpen}
            setIsAutomateModalOpen={setIsAutomateModalOpen}
          />
        </div>
      )}
      {!isReadOnly && (
        <div style={{ width: '94%' }} className="relative flex justify-end m-auto">
          <div className="absolute right-0 top-5">
            <AddField />
          </div>
        </div>
      )}
      <div
        className={cn(`ag-theme-alpine m-auto ${styles['custom-ag-theme']}`, {
          'mt-12': isReadOnly,
          'pb-12': !isReadOnly,
        })}
        style={{ height: 'calc((100vh - 150px)', width: '90%' }}
      >
        {!rowData && <>Loading...</>}
        {rowData && (
          <AgGridReact
            rowData={rowData}
            ref={gridApi}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
            }}
            onGridReady={(e) => {
              setGridIsReady(true);
            }}
            rowDragManaged={true}
            animateRows={true}
            readOnlyEdit={true}
            onCellEditRequest={onCellEditRequest}
            rowGroupPanelShow="onlyWhenGrouping"
            getRowClass={({ data }) => {
              // eslint-disable-next-line react/prop-types
              const classes: string[] = [];
              const selected = data.rowSelected;
              if (data?.completed && !selected) classes.push('opacity-75');
              if (selected) classes.push('row-selected');
              return classes.join(' ');
            }}
            alwaysShowHorizontalScroll
            onColumnMoved={handleChangeColumn}
            onColumnVisible={handleChangeColumn}
            onColumnResized={handleChangeColumn}
            onColumnPinned={handleChangeColumn}
            // onFilterChanged={handleChangeColumn}
            // onFilterModified={handleChangeColumn}
            gridOptions={{
              suppressHorizontalScroll: false,
              suppressScrollOnNewData: true,
            }}
            maintainColumnOrder={true}
            treeData={true}
            getDataPath={getDataPath}
            autoGroupColumnDef={autoGroupColumnDef}
            groupDefaultExpanded={1}
            getMainMenuItems={getMainMenuItems}
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            excludeChildrenWhenTreeDataFiltering
            isExternalFilterPresent={() => !!Object.keys(filterValues).length}
            doesExternalFilterPass={handleFilterNodes}
          ></AgGridReact>
        )}
        <Modal isOpen={!!router.query.taskId} onClose={handleTaskViewClose} motionPreset="none">
          <ModalOverlay />
          <ModalContent
            style={{
              position: 'initial',
              maxWidth: '120rem',
            }}
            className={`min-w-768 h-vh90 bg-white rounded-lg static`}
          >
            {router.query.taskId && (
              <TaskEdit
                id={router?.query?.taskId as string}
                onDelete={onToggleDeletePrompt(true)}
                onDuplicate={onToggleDuplicatePrompt(true)}
                onClose={handleTaskViewClose}
                onChangeTask={(e) => {
                  if (!e.task.isSubtask && e.task.column && e.task.id) {
                    setItem({
                      columnId: e.task.column,
                      item: e.task as any,
                      itemId: e.task.id,
                    });
                  } else if (e.task.isSubtask && e.task.column && e.task.id) {
                    if (e.task.parent) {
                      const sanitisedTask = Object.assign({}, e.task, {
                        parent: e.task.parent._id,
                      });
                      setItem({
                        columnId: e.task.column,
                        item: sanitisedTask as any,
                        itemId: e.task.id,
                      });
                    }
                  }
                  if (e.task?.fields) {
                    setFields(e.task.fields);
                  }
                }}
              />
            )}
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isDeletePromptOpen}
          onClose={() => {
            setIsDeletePromptOpen(false);
          }}
        >
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsDeletePromptOpen(false);
                }}
                className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
              >
                Cancel
              </button>
              <button
                className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                onClick={handleDeleteTask}
              >
                Delete
              </button>
            </div>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isDuplicatePromptOpen}
          onClose={() => {
            setIsDuplicatePromptOpen(false);
          }}
        >
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsDuplicatePromptOpen(false);
                }}
                className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
              >
                Cancel
              </button>
              <button
                className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                onClick={handleDuplicateTask}
              >
                Duplicate
              </button>
            </div>
          </ModalContent>
        </Modal>
        <DeleteField
          handleDeleteField={handleDeleteCustomField}
          isDeleteModalOpen={isFieldDeletePromptOpen}
          setIsDeleteModalOpen={setIsFieldDeletePromptOpen}
        />
      </div>
    </div>
  );
};

const boardSelector = (s: KanbanState) => ({
  columns: s.columns,
  onAddNewColumn: s.onAddNewColumn,
  currentSelectedItem: s.currentSelectedItem,
  onCloseCurrentSelectedItem: s.onCloseCurrentSelectedItem,
  isReadOnly: s.isReadOnly,
  onUpdateItemPlacement: s.onUpdateItemPlacement,
  onChangeColumnOrder: s.onChangeColumnOrder,
  setItem: s.setItem,
  onDeleteItem: s.onDeleteItem,
  onDuplicateItem: s.onDuplicateItem,
  onChangeCurrentSelectedItem: s.onChangeCurrentSelectedItem,
  onUpdateItemField: s.onUpdateItemField,
  onMarkAsComplete: s.onMarkAsComplete,
});
